import React from "react";
import Layout from "../components/Layout/Layout";
import { useNavigate } from "react-router-dom";
import { FiAward, FiCompass, FiTag, FiArrowRight } from "react-icons/fi";

const About = () => {
  const navigate = useNavigate();

  const PALETTE = {
    DARK_NAVY: "#1C2E4A",
    ACCENT_BROWN: "#7C4F2B",
    LIGHT_CREAM: "#FAF8F5",
  };

  return (
    <Layout title={"Russh Berry | About Us | Premium Men's Clothing"}>
      <div
        className="min-h-screen pt-24 pb-20 transition-colors duration-500"
        style={{ backgroundColor: PALETTE.LIGHT_CREAM }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12">
          {/* HERO HEADER SECTION */}
          <div className="text-center max-w-3xl mx-auto mb-16 sm:mb-20">
            <p
              className="text-xs sm:text-sm font-semibold tracking-[0.3em] uppercase mb-3"
              style={{ color: PALETTE.ACCENT_BROWN }}
            >
              The Brand Story
            </p>
            <h1
              className="text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight mb-6 leading-tight"
              style={{ color: PALETTE.DARK_NAVY }}
            >
              About Russh Berry
            </h1>
            <div
              className="w-16 h-[1px] mx-auto mb-6"
              style={{ backgroundColor: PALETTE.ACCENT_BROWN }}
            />
            <p className="text-gray-600 text-sm sm:text-base font-light leading-relaxed">
              A premium destination dedicated exclusively to modern menswear,
              founded on the belief that luxury should be attainable without
              compromise.
            </p>
          </div>

          {/* MAIN EDITORIAL STORY BLOCK */}
          <div className="bg-white p-8 sm:p-12 lg:p-16 shadow-xl border border-gray-100/80 mb-16 relative overflow-hidden">
            <div
              className="absolute top-0 left-0 w-full h-1.5"
              style={{ backgroundColor: PALETTE.DARK_NAVY }}
            />

            <div className="max-w-4xl mx-auto space-y-8 text-gray-700 font-light text-base sm:text-lg leading-relaxed">
              <p
                className="first-letter:text-5xl first-letter:font-light first-letter:mr-3 first-letter:float-left"
                style={{ firstLetter: { color: PALETTE.DARK_NAVY } }}
              >
                Russh Berry is a premium destination dedicated exclusively to
                modern menswear, founded on the belief that luxury should be
                attainable without compromise. Our mission is to bring together
                exceptional craftsmanship, contemporary design, and superior
                fabrics to create garments that elevate everyday style. From
                impeccably tailored shirts and elegant kurtas to versatile
                jackets and trend-forward T-shirts, every piece in our
                collection is thoughtfully designed to offer the perfect balance
                of comfort, sophistication, and value.
              </p>

              <p>
                We take pride in working with high-quality materials such as
                fine cotton, breathable linen, and other carefully selected
                premium fabrics that ensure lasting durability and a refined
                feel. Inspired by both timeless classics and the latest fashion
                trends, our collections are created for men who appreciate
                understated elegance and effortless versatility. Whether you are
                dressing for work, leisure, celebrations, or travel, Russh Berry
                offers styles that seamlessly adapt to every occasion.
              </p>

              <p>
                At the heart of Russh Berry lies a commitment to affordable
                luxury. We believe that every man deserves access to clothing
                that reflects confidence, individuality, and impeccable taste.
                By combining meticulous attention to detail with fair pricing,
                we strive to make premium fashion an everyday experience. As we
                continue to grow, our promise remains unchanged: to deliver
                exceptional menswear that empowers you to look your best and
                express your unique style with confidence.
              </p>
            </div>
          </div>

          {/* 3 PILLAR FEATURE GRID */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10 items-stretch mb-16">
            {/* PILLAR 1 */}
            <div
              className="bg-white p-8 sm:p-10 shadow-md border border-gray-100/80 transition-all duration-300 hover:shadow-xl flex flex-col justify-between"
              style={{ borderBottom: `4px solid ${PALETTE.ACCENT_BROWN}` }}
            >
              <div>
                <div
                  className="w-12 h-12 rounded-full bg-[#FAF8F5] flex items-center justify-center mb-6"
                  style={{ color: PALETTE.ACCENT_BROWN }}
                >
                  <FiAward size={22} />
                </div>
                <h3
                  className="text-lg sm:text-xl font-light tracking-wider uppercase mb-3"
                  style={{ color: PALETTE.DARK_NAVY }}
                >
                  Exceptional Craftsmanship
                </h3>
                <p className="text-gray-600 font-light text-sm leading-relaxed">
                  Working with fine cotton, breathable linen, and premium
                  fabrics, every piece—from tailored shirts to elegant kurtas—is
                  designed for lasting durability and a refined feel.
                </p>
              </div>
            </div>

            {/* PILLAR 2 */}
            <div
              className="bg-white p-8 sm:p-10 shadow-md border border-gray-100/80 transition-all duration-300 hover:shadow-xl flex flex-col justify-between"
              style={{ borderBottom: `4px solid ${PALETTE.DARK_NAVY}` }}
            >
              <div>
                <div
                  className="w-12 h-12 rounded-full bg-[#FAF8F5] flex items-center justify-center mb-6"
                  style={{ color: PALETTE.DARK_NAVY }}
                >
                  <FiCompass size={22} />
                </div>
                <h3
                  className="text-lg sm:text-xl font-light tracking-wider uppercase mb-3"
                  style={{ color: PALETTE.DARK_NAVY }}
                >
                  Effortless Versatility
                </h3>
                <p className="text-gray-600 font-light text-sm leading-relaxed">
                  Inspired by timeless classics and contemporary trends, our
                  collections adapt seamlessly to your lifestyle—whether
                  dressing for work, leisure, celebrations, or travel.
                </p>
              </div>
            </div>

            {/* PILLAR 3 */}
            <div
              className="bg-white p-8 sm:p-10 shadow-md border border-gray-100/80 transition-all duration-300 hover:shadow-xl flex flex-col justify-between"
              style={{ borderBottom: `4px solid ${PALETTE.ACCENT_BROWN}` }}
            >
              <div>
                <div
                  className="w-12 h-12 rounded-full bg-[#FAF8F5] flex items-center justify-center mb-6"
                  style={{ color: PALETTE.ACCENT_BROWN }}
                >
                  <FiTag size={22} />
                </div>
                <h3
                  className="text-lg sm:text-xl font-light tracking-wider uppercase mb-3"
                  style={{ color: PALETTE.DARK_NAVY }}
                >
                  Attainable Luxury
                </h3>
                <p className="text-gray-600 font-light text-sm leading-relaxed">
                  By combining meticulous attention to detail with fair pricing,
                  we make premium menswear an everyday experience without
                  compromising on quality or style.
                </p>
              </div>
            </div>
          </div>

          {/* BOTTOM CTA SECTION */}
          <div
            className="text-center p-10 sm:p-16 border shadow-sm relative overflow-hidden"
            style={{
              backgroundColor: PALETTE.DARK_NAVY,
              borderColor: PALETTE.DARK_NAVY,
            }}
          >
            <div className="relative z-10 max-w-2xl mx-auto">
              <p
                className="text-xs sm:text-sm font-semibold tracking-[0.3em] uppercase mb-3"
                style={{ color: PALETTE.ACCENT_BROWN }}
              >
                Experience The Craft
              </p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light tracking-tight text-white mb-6">
                Elevate Your Everyday Style
              </h2>
              <p className="text-gray-300 font-light text-sm sm:text-base mb-8">
                Discover our latest menswear collections, designed to empower
                you to look your best and express your individuality with
                confidence.
              </p>
              <button
                onClick={() => navigate("/products")}
                className="inline-flex items-center gap-3 px-10 py-4 text-xs font-semibold tracking-[0.25em] uppercase transition-all duration-300 shadow-lg hover:scale-105"
                style={{
                  backgroundColor: PALETTE.ACCENT_BROWN,
                  color: "#FFFFFF",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#FFFFFF";
                  e.currentTarget.style.color = PALETTE.DARK_NAVY;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = PALETTE.ACCENT_BROWN;
                  e.currentTarget.style.color = "#FFFFFF";
                }}
              >
                <span>Explore The Collection</span>
                <FiArrowRight className="text-base" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default About;
